import { isEmpty } from '@intelligence/v3/utils/common';
import { formatDateStr } from '@intelligence/v3/utils/date';
import { getGenreKeyIntelligence } from '@intelligence/v3/utils/genre';
import { getMetricValueIntelligence } from '@intelligence/v3/pages/CasualGenreProfile/utils';

import type { TDataSourceType, TDimension, TMetricType } from '@intelligence/v3/pages/CasualGenreProfile/types';

export const getBarOrPieData = (params: {
  isHorizontalBar?: boolean;
  genreSource: IGenreSource;
  genreDimension: TDimension;
  dataSource: TDataSourceType;
  metric: TMetricType;
  list?: ICasualGenreProfileMetricData[] | null;
}) => {
  const isHorizontalBar = params?.isHorizontalBar ?? false;
  const genreSource = params?.genreSource || '';
  const genreDimension = params?.genreDimension || '';
  const dataSource = params?.dataSource || '';
  const metric = params?.metric || '';
  const list = params?.list || [];

  if (isEmpty(list)) {
    return [];
  }

  // 先获取原始数据
  const rawData = list!.map((item: any) => {
    const itemKey = getGenreKeyIntelligence(genreSource, genreDimension);
    const itemName = item?.[itemKey];
    const itemValue = getMetricValueIntelligence({
      dataSource,
      metric,
      data: item,
    });

    return {
      name: itemName,
      value: itemValue,
      payload: { ...item },
    };
  });

  // 按指标值降序排序，无效数值排在最后
  const sortedData = rawData.sort((a, b) => {
    const aValue = a?.value ?? Number.NaN;
    const bValue = b?.value ?? Number.NaN;
    const aValid = !Number.isNaN(aValue);
    const bValid = !Number.isNaN(bValue);

    if (!aValid && !bValid) {
      return 0;
    }
    if (!aValid && bValid) {
      return 1;
    }
    if (aValid && !bValid) {
      return -1;
    }

    return bValue - aValue;
  });

  // 取前10名
  const top10 = sortedData.slice(0, 10);
  if (isHorizontalBar) {
    top10.reverse();
  }

  // 其余的合并为 Others
  const others = sortedData.slice(10);
  if (others.length > 0) {
    const othersValue = others.reduce((sum, item) => {
      const itemValue = item?.value ?? 0;
      return sum + itemValue;
    }, 0);

    if (isHorizontalBar) {
      top10.unshift({
        name: 'others',
        value: othersValue,
        payload: { rank: 0 },
      });
    } else {
      top10.push({
        name: 'others',
        value: othersValue,
        payload: { rank: 0 },
      });
    }
  }

  return top10;
};

export const getLineData = (params: {
  genreSource: IGenreSource;
  genreDimension: TDimension;
  dataSource: TDataSourceType;
  metric: TMetricType;
  list?: ICasualGenreProfileMetricData[] | null;
}): ILineChartData[] => {
  const genreSource = params?.genreSource || '';
  const genreDimension = params?.genreDimension || '';
  const dataSource = params?.dataSource || '';
  const metric = params?.metric || '';
  const list = params?.list || [];

  if (isEmpty(list)) {
    return [];
  }

  return list!.map((item: any) => {
    const itemDate = item?.date || '';
    const itemKey = getGenreKeyIntelligence(genreSource, genreDimension);
    const itemName = item?.[itemKey];
    const itemValue = getMetricValueIntelligence({
      dataSource,
      metric,
      data: item,
    });

    return {
      date: itemDate,
      name: itemName,
      value: itemValue,
      payload: { ...item },
    };
  });
};

export const xAxisLabelFormatterOfTrendLineChartWithGranularity = (granularity: IGranularity, params: string) => {
  if (granularity === 'monthly') {
    return formatDateStr(params, 'YYYY-MM');
  }

  if (granularity === 'daily') {
    return formatDateStr(params, 'YYYY-MM-DD');
  }

  return params || '';
};
